import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import axios from "axios";

const FrameworkPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth(); 
  const [frameworks, setFrameworks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchFrameworks = async () => {
      try {
        const accessToken = localStorage.getItem("access_token");
        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}maturity/my-frameworks/`,
          { headers: { Authorization: `Bearer ${accessToken}` } }
        );

        setFrameworks(res.data);
        setLoading(false);
      } catch (err) {
        console.error(err);
        setError("Failed to load your assessments.");
        setLoading(false);
      }
    };

    if (user) fetchFrameworks();
  }, [user]);

  const handleFrameworkClick = (framework) => {
    const code = framework.code.toUpperCase();
    if (code.includes("COBIT")) navigate("/cobit/instructions");
    else if (code.includes("BCM")) navigate("/bcm/instructions");
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-6">
        <p className="text-lg text-gray-600 animate-pulse">
          Loading your assessments...
        </p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-6">
        <p className="text-lg text-red-500">{error}</p>
      </div>
    );
  }

  if (frameworks.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-6">
        <p className="text-lg text-gray-700 text-center">
          No assessments have been assigned to your account. Please contact your administrator.
        </p>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 px-6 py-20">
      {/* Hero Section */}
      <div className="text-center mb-16 max-w-3xl mx-auto">
        <h1 className="text-5xl font-bold text-[#FF4D00] mb-4">
          Welcome to Your Assessments
        </h1>
        <p className="text-lg text-gray-700 mb-2">
          Here you can view all frameworks assigned to your account. Click on a framework to start or continue your assessment.
        </p>
        <p className="text-gray-600">
          Completing these assessments helps your organization manage IT governance and business continuity effectively.
        </p>
      </div>

      {/* Framework Cards */}
      <div className="flex flex-wrap gap-8 justify-center items-center">
        {frameworks.map((f) => {
          const code = f.code.toUpperCase();
          const bgClass = code.includes("COBIT")
            ? "bg-gradient-to-r from-[#FF4D00] to-orange-600"
            : code.includes("BCM")
            ? "bg-gradient-to-r from-amber-400 to-amber-600"
            : "bg-gray-400";

          return (
            <div
              key={f.id}
              className="flex flex-col justify-between bg-white rounded-xl shadow-xl p-6 w-80 h-96 hover:shadow-2xl transition duration-300"
            >
              <div className="overflow-hidden">
                <h2 className="text-2xl font-bold mb-2 flex items-center gap-2">
                  {code.includes("COBIT") && "🚀"} 
                  {code.includes("BCM") && "🔒"} 
                  {f.name}
                </h2>
                <p className="text-gray-700 text-sm overflow-y-auto h-48">
                  {f.description}
                </p>
              </div>
              <button
                onClick={() => handleFrameworkClick(f)}
                className={`${bgClass} mt-4 px-6 py-3 rounded-lg text-white font-semibold hover:scale-105 transform transition duration-300`}
              >
                Start Assessment
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default FrameworkPage;
